import { apiClient } from '@/utils/api'

export type SeasonStatus = 'upcoming' | 'active' | 'completed'
export type RegistrationMode = 'anyone' | 'group_members_only'

export interface JoiningFee {
  amount: number       // Amount in smallest currency unit (e.g., cents/pence)
  currency: string     // ISO 4217 currency code (e.g., "GBP", "USD", "EUR")
  description?: string // Optional description (e.g., "Covers court fees")
}

// Denormalized bracket summary stored on season
export interface BracketSummary {
  bracketId: string
  name: string
  playerCount: number
}

export interface Season {
  seasonId: string
  leagueId: string
  groupId: string
  name: string
  startDate: string
  endDate: string
  status: SeasonStatus
  signupsOpen: boolean
  registrationMode: RegistrationMode
  termsAndConditions?: string   // Optional T&C text for signup
  joiningFee?: JoiningFee       // Optional joining fee
  /** Whether to show group bank details on registration */
  showBankDetails?: boolean
  /** Override payment reference for this season (uses group default if not set) */
  paymentReference?: string
  /** Maximum games a player can play against the same opponent this season. null = unlimited */
  maxGamesPerOpponent?: number | null
  // Denormalized stats (available in summary endpoints)
  bracketCount?: number
  totalPlayerCount?: number
  brackets?: BracketSummary[]
  leagueName?: string
  leagueFormat?: string
  createdAt: string
  updatedAt: string
}

interface SeasonsResponse {
  data: Season[]
  meta: {
    total: number
  }
}

interface SeasonResponse {
  data: Season
}

export const seasonService = {
  async list(leagueId: string): Promise<Season[]> {
    const response = await apiClient.authGet<SeasonsResponse>(
      `/leagues/${leagueId}/seasons`
    )
    return response.data
  },

  async create(
    leagueId: string,
    data: { 
      name: string
      startDate: string
      endDate: string
      signupsOpen?: boolean
      registrationMode?: RegistrationMode
      termsAndConditions?: string
      joiningFee?: JoiningFee
      showBankDetails?: boolean
      paymentReference?: string
      maxGamesPerOpponent?: number | null
    }
  ): Promise<Season> {
    const response = await apiClient.authPost<SeasonResponse>(
      `/leagues/${leagueId}/seasons`,
      data
    )
    return response.data
  },

  async update(
    leagueId: string,
    seasonId: string,
    data: { 
      name?: string
      startDate?: string
      endDate?: string
      signupsOpen?: boolean
      registrationMode?: RegistrationMode
      termsAndConditions?: string | null  // null to clear
      joiningFee?: JoiningFee | null      // null to clear
      showBankDetails?: boolean
      paymentReference?: string | null    // null to clear (use group default)
      maxGamesPerOpponent?: number | null // null to clear (unlimited)
    }
  ): Promise<Season> {
    const response = await apiClient.authPut<SeasonResponse>(
      `/leagues/${leagueId}/seasons/${seasonId}`,
      data
    )
    return response.data
  },

  async delete(leagueId: string, seasonId: string): Promise<void> {
    await apiClient.authDelete(`/leagues/${leagueId}/seasons/${seasonId}`)
  },

  async listPublic(leagueId: string): Promise<Season[]> {
    const response = await apiClient.get<SeasonsResponse>(
      `/leagues/${leagueId}/seasons/public`
    )
    return response.data
  },

  // Optimized group summary endpoints
  async getGroupSummary(groupId: string): Promise<GroupSeasonsSummary> {
    const response = await apiClient.authGet<{ data: GroupSeasonsSummary }>(
      `/groups/${groupId}/seasons/summary`
    )
    return response.data
  },

  async getGroupSummaryPublic(groupId: string): Promise<GroupSeasonsSummary> {
    const response = await apiClient.get<{ data: GroupSeasonsSummary }>(
      `/groups/${groupId}/seasons/summary/public`
    )
    return response.data
  },
}

// Types for optimized group seasons summary
export interface BracketWithPlayerCount {
  bracketId: string
  name: string
  playerCount: number
}

export interface SeasonWithBrackets extends Season {
  league: {
    leagueId: string
    name: string
    format: string
  }
  brackets: BracketWithPlayerCount[]
  totalPlayers: number
}

// User's registration status for a signup season
export type UserRegistrationStatus = 'none' | 'pending' | 'confirmed' | 'rejected'

// Extended type for signup seasons with user-specific state
export interface SignupSeasonWithState extends SeasonWithBrackets {
  myRegistrationStatus: UserRegistrationStatus
  isAlreadyInSeason: boolean
  canRegister: boolean // Based on registrationMode + membership/public status
}

// Organiser info for display
export interface OrganiserInfo {
  userId: string
  displayName: string
  avatar?: string
}

export interface GroupSeasonsSummary {
  activeSeasons: SeasonWithBrackets[]
  upcomingSignups: SignupSeasonWithState[]
  // Group stats
  leagueCount: number
  memberCount: number
  organisers: OrganiserInfo[]
  // Context info
  isMember: boolean
}
